/**
 * The `style` of a vector layer drawn from config (an external data layer, or a
 * GeoJSON layer added by URL) holds MapLibre paint keys for the way it is
 * drawn, plus a few of our own: `iconSize` and `label*` for points drawn as an
 * icon, and `outlineWidth` for areas.
 *
 * MapLibre rejects a layer whose paint holds a key from another layer type
 * (`circle-color` on a line), so a style is narrowed to the keys of the way the
 * layer is drawn: when Draw as changes in the admin console, and again when the
 * map builds the layer.
 */

export type LayerStyle = Record<string, any>;

/** How an area's outline is drawn wider than MapLibre's 1 px fill outline: as a line layer over the fill. */
export const AREA_OUTLINE_WIDTH = "outlineWidth";

/** Id of the line layer that draws an area's wide outline. The `::` suffix makes clicks and visibility resolve to the area's layer. */
export const areaOutlineLayerId = (layerId: string) => `${layerId}::outline`;

const KEYS_FOR: Record<string, (key: string) => boolean> = {
  // A point is a symbol layer when it has an icon, a circle layer when it has none.
  symbol: (key) => key.startsWith("circle-") || key.startsWith("icon") || key.startsWith("label"),
  circle: (key) => key.startsWith("circle-") || key.startsWith("icon") || key.startsWith("label"),
  line: (key) => key.startsWith("line-"),
  fill: (key) => key.startsWith("fill-") || key === AREA_OUTLINE_WIDTH || key === "opacity"
};

/** The part of `style` that applies when the layer is drawn as `geometry`. */
export const styleForGeometry = (style: LayerStyle | undefined, geometry: string | undefined): LayerStyle => {
  const keep = geometry ? KEYS_FOR[geometry] : undefined;
  if (!style || !keep) return { ...style };
  return Object.fromEntries(Object.entries(style).filter(([key]) => keep(key)));
};

const withPrefix = (style: LayerStyle | undefined, prefix: string): LayerStyle =>
  Object.fromEntries(Object.entries(style ?? {}).filter(([key]) => key.startsWith(prefix)));

/** Paint for a line layer: only `line-*` keys. */
export const linePaint = (style: LayerStyle | undefined): LayerStyle => withPrefix(style, "line-");

/** Paint for a circle layer: only `circle-*` keys. */
export const circlePaint = (style: LayerStyle | undefined): LayerStyle => withPrefix(style, "circle-");

/**
 * Paint for an area, and the line paint of its outline when that is wider than
 * 1 px. `outlineWidth` 0 hides the outline; unset keeps MapLibre's 1 px one.
 */
export const areaPaint = (style: LayerStyle | undefined): { fill: LayerStyle; outline: LayerStyle | null } => {
  const fill = withPrefix(style, "fill-");
  const width = style?.[AREA_OUTLINE_WIDTH];
  if (typeof width !== "number") return { fill, outline: null };
  if (width <= 0) return { fill: { ...fill, "fill-outline-color": "rgba(0, 0, 0, 0)" }, outline: null };
  if (width <= 1) return { fill, outline: null };
  const color = style?.["fill-outline-color"] ?? style?.["fill-color"];
  return { fill, outline: { "line-width": width, ...(color !== undefined ? { "line-color": color } : {}) } };
};

// `$type` folds Multi* geometries into their base family, so these also match
// MultiPoint, MultiLineString and MultiPolygon features.
export const POINT_FILTER = ["==", "$type", "Point"];
export const LINE_FILTER = ["==", "$type", "LineString"];
export const POLYGON_FILTER = ["==", "$type", "Polygon"];

/**
 * The features a layer drawn as `geometry` should draw: points (and multipoints)
 * for a point layer, lines (and multilines) for a line layer, polygons (and
 * multipolygons) for a fill. Without it a circle layer also dots every vertex of
 * a line or polygon, and a line layer traces polygon outlines.
 */
export const geometryFamilyFilter = (geometry: string | undefined): unknown[] | undefined => {
  if (geometry === "symbol" || geometry === "circle") return POINT_FILTER;
  if (geometry === "line") return LINE_FILTER;
  if (geometry === "fill") return POLYGON_FILTER;
  return undefined;
};

/** A style value the simple fields can show: a plain number or colour, not a MapLibre expression. */
export const isPlainStyleValue = (value: unknown): value is string | number => typeof value === "string" || typeof value === "number";
